import React, { useState } from 'react';
import { signInWithRedirect, signIn, signUp, confirmSignUp } from 'aws-amplify/auth';
import { useAuth } from '../components/AuthProvider';

// Feature flags for social login providers (configured at build time)
const ENABLE_APPLE = import.meta.env.VITE_ENABLE_APPLE_IDP === 'true';
const ENABLE_GOOGLE = import.meta.env.VITE_ENABLE_GOOGLE_IDP === 'true';

type AuthMode = 'signIn' | 'signUp' | 'confirmSignUp';

export function SignIn() {
  const { sessionExpired } = useAuth();
  const [mode, setMode] = useState<AuthMode>('signIn');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [verificationCode, setVerificationCode] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(false);

  const handleEmailSignIn = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    setIsLoading(true);

    try {
      await signIn({ username: email, password });
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Sign-in failed. Please try again.');
    } finally {
      setIsLoading(false);
    }
  };

  const handleEmailSignUp = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);

    if (password !== confirmPassword) {
      setError('Passwords do not match.');
      return;
    }

    if (password.length < 12) {
      setError('Password must be at least 12 characters.');
      return;
    }

    setIsLoading(true);

    try {
      await signUp({ username: email, password });
      setMode('confirmSignUp');
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Sign-up failed. Please try again.');
    } finally {
      setIsLoading(false);
    }
  };

  const handleConfirmSignUp = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    setIsLoading(true);

    try {
      await confirmSignUp({ username: email, confirmationCode: verificationCode });
      // After confirmation, sign in automatically
      await signIn({ username: email, password });
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Verification failed. Please try again.');
    } finally {
      setIsLoading(false);
    }
  };

  const handleAppleSignIn = async () => {
    try {
      await signInWithRedirect({ provider: 'Apple' });
    } catch (err) {
      setError('Apple sign-in failed. Please try again.');
    }
  };

  const handleGoogleSignIn = async () => {
    try {
      await signInWithRedirect({ provider: 'Google' });
    } catch (err) {
      setError('Google sign-in failed. Please try again.');
    }
  };

  return (
    <main className="sign-in-page" role="main" aria-labelledby="sign-in-heading">
      <div className="sign-in-container">
        <h1 id="sign-in-heading">AuditMyPay</h1>
        <p className="sign-in-subtitle">
          Payslip audit for Australian medical professionals
        </p>

        {sessionExpired && (
          <div className="session-expired-message" role="alert" aria-live="polite">
            Your session has expired. Please sign in again.
          </div>
        )}

        {error && (
          <div className="auth-error-message" role="alert" aria-live="assertive">
            {error}
          </div>
        )}

        {/* Email/Password Sign In */}
        {mode === 'signIn' && (
          <form onSubmit={handleEmailSignIn} aria-label="Sign in with email">
            <div className="form-field">
              <label htmlFor="email">Email</label>
              <input
                id="email"
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                required
                autoComplete="email"
                aria-required="true"
              />
            </div>

            <div className="form-field">
              <label htmlFor="password">Password</label>
              <input
                id="password"
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                required
                autoComplete="current-password"
                aria-required="true"
              />
            </div>

            <button
              type="submit"
              disabled={isLoading}
              className="sign-in-button sign-in-email"
              aria-label="Sign in with email and password"
            >
              {isLoading ? 'Signing in...' : 'Sign In'}
            </button>

            <p className="auth-switch">
              Don't have an account?{' '}
              <button
                type="button"
                onClick={() => { setMode('signUp'); setError(null); }}
                className="auth-switch-link"
              >
                Create one
              </button>
            </p>
          </form>
        )}

        {/* Email/Password Sign Up */}
        {mode === 'signUp' && (
          <form onSubmit={handleEmailSignUp} aria-label="Create an account">
            <div className="form-field">
              <label htmlFor="signup-email">Email</label>
              <input
                id="signup-email"
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                required
                autoComplete="email"
                aria-required="true"
              />
            </div>

            <div className="form-field">
              <label htmlFor="signup-password">Password</label>
              <input
                id="signup-password"
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                required
                minLength={12}
                autoComplete="new-password"
                aria-required="true"
                aria-describedby="password-hint"
              />
              <p id="password-hint" className="field-hint">
                Minimum 12 characters with uppercase, lowercase, and numbers.
              </p>
            </div>

            <div className="form-field">
              <label htmlFor="signup-confirm-password">Confirm Password</label>
              <input
                id="signup-confirm-password"
                type="password"
                value={confirmPassword}
                onChange={(e) => setConfirmPassword(e.target.value)}
                required
                autoComplete="new-password"
                aria-required="true"
              />
            </div>

            <button
              type="submit"
              disabled={isLoading}
              className="sign-in-button sign-in-email"
              aria-label="Create account"
            >
              {isLoading ? 'Creating account...' : 'Create Account'}
            </button>

            <p className="auth-switch">
              Already have an account?{' '}
              <button
                type="button"
                onClick={() => { setMode('signIn'); setError(null); }}
                className="auth-switch-link"
              >
                Sign in
              </button>
            </p>
          </form>
        )}

        {/* Email Verification Code */}
        {mode === 'confirmSignUp' && (
          <form onSubmit={handleConfirmSignUp} aria-label="Verify your email">
            <p className="verification-message">
              We've sent a verification code to <strong>{email}</strong>.
            </p>

            <div className="form-field">
              <label htmlFor="verification-code">Verification Code</label>
              <input
                id="verification-code"
                type="text"
                inputMode="numeric"
                value={verificationCode}
                onChange={(e) => setVerificationCode(e.target.value)}
                required
                autoComplete="one-time-code"
                aria-required="true"
              />
            </div>

            <button
              type="submit"
              disabled={isLoading}
              className="sign-in-button sign-in-email"
              aria-label="Verify email and sign in"
            >
              {isLoading ? 'Verifying...' : 'Verify and Sign In'}
            </button>
          </form>
        )}

        {/* Social Login Divider (only shown if any social provider is enabled) */}
        {(ENABLE_APPLE || ENABLE_GOOGLE) && mode === 'signIn' && (
          <>
            <div className="sign-in-divider" aria-hidden="true">
              <span>or</span>
            </div>

            <div className="sign-in-buttons" role="group" aria-label="Social sign-in options">
              {ENABLE_APPLE && (
                <button
                  onClick={handleAppleSignIn}
                  className="sign-in-button sign-in-apple"
                  aria-label="Sign in with Apple"
                >
                  Sign in with Apple
                </button>
              )}

              {ENABLE_GOOGLE && (
                <button
                  onClick={handleGoogleSignIn}
                  className="sign-in-button sign-in-google"
                  aria-label="Sign in with Google"
                >
                  Sign in with Google
                </button>
              )}
            </div>
          </>
        )}

        <footer className="sign-in-footer">
          <a
            href="/privacy-policy"
            className="privacy-policy-link"
            aria-label="View privacy policy"
          >
            Privacy Policy
          </a>
        </footer>
      </div>
    </main>
  );
}
